import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { useFormContext } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { addDays, getDay, isSameDay, parseISO } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { toast } from 'sonner'

import { PayPalCheckoutButtons } from '@/components/paypal-checkout-buttons'
import { PACKAGES } from '@/constants'
import { PAYPAL_SCRIPT_OPTIONS } from '@/lib/paypal'
import { BookExtendForm } from '@/pages/customer/extend-booking'
import bookingService from '@/services/booking-service'
import holidayService from '@/services/holiday-service'
import { useState } from 'react'

interface PaymentFormProps {
	selectedBooking: UserBooking
	onExtendConfirm: () => void
}

export const PaymentForm = ({ selectedBooking, onExtendConfirm }: PaymentFormProps) => {
	const form = useFormContext<BookExtendForm>()

	const [disabledDates, setDisabledDates] = useState<Holiday[]>([])
	const [endDate, setEndDate] = useState<Date | null>(null)

	const { mutate: updateBooking } = useMutation({
		mutationKey: 'update-booking',
		mutationFn: ({
			id,
			data,
		}: {
			id: number
			data: { end: string; rent_price: number; paypal_code: string; title: string }
		}) => bookingService.updateBooking(id, data),
	})

	const selectedPackage = PACKAGES.find(p => p.id === Number(form.watch('package')))!

	const calcWorkingDays = (fromDate: Date, days: number, holidays = disabledDates) => {
		let count = 1
		const workingDate = new Date(fromDate)

		while (count < days) {
			workingDate.setDate(workingDate.getDate() + 1)

			if (
				workingDate.getDay() !== 0 &&
				!holidays.find(dDate => isSameDay(parseISO(dDate.holiday_date), workingDate))
			) {
				count++
			}
		}

		return workingDate
	}

	useQuery('fetch-all-holidays', holidayService.fetchAllHolidays, {
		enabled: !!selectedPackage,
		onSuccess: res => {
			setDisabledDates(res.data)

			const startDate = new Date(selectedBooking.start)
			startDate.setDate(startDate.getDate() + 1)
			startDate.setHours(0, 0, 0, 0)

			const bookingEndDateUTC = toZonedTime(parseISO(selectedBooking.end), 'UTC')
			let updatedEndDate = addDays(bookingEndDateUTC, 1)

			if (getDay(updatedEndDate) === 0) {
				updatedEndDate = addDays(updatedEndDate, 1)
			}

			const endDate = calcWorkingDays(updatedEndDate, selectedPackage!.duration, res.data)
			setEndDate(new Date(endDate))
		},
		onError: () => {
			toast.error('Error fetching holidays')
		},
	})

	const createOrder = async (_data: any, actions: any): Promise<string> => {
		return actions.order.create({
			purchase_units: [
				{
					description: `Booking stand ${selectedPackage.title}`,
					amount: {
						currency_code: 'EUR',
						value: selectedPackage.price,
					},
				},
			],
			application_context: {
				shipping_preference: 'NO_SHIPPING',
			},
		})
	}

	const confirmExtension = (paypalCode: string) => {
		if (!endDate) return

		const payload = {
			end: endDate.toISOString(),
			rent_price: selectedPackage.price,
			paypal_code: paypalCode,
			title: selectedPackage.title,
		}

		updateBooking(
			{ id: selectedBooking.id, data: payload },
			{
				onSuccess: () => {
					onExtendConfirm()
				},
				onError: (error: any) => {
					toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
				},
			},
		)
	}

	const onApprove = async (_data: any, actions: any): Promise<void> => {
		const details = await actions.order.capture()
		const paypalCode = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id

		if (paypalCode) {
			confirmExtension(paypalCode)
		}
	}

	return (
		<div className="mt-[30px] lg:mt-12 flex flex-col">
			<h5 className="text-secondary lg:text-center max-lg:text-xs font-medium">Zur Zahlung:</h5>
			<div className="mt-5">
				<PayPalScriptProvider options={PAYPAL_SCRIPT_OPTIONS}>
					<PayPalCheckoutButtons
						createOrder={createOrder}
						onApprove={onApprove}
						applePay={{
							amount: Number(selectedPackage.price),
							label: `Booking stand ${selectedPackage.title}`,
							onBeforePay: () => Boolean(endDate),
							onApproved: confirmExtension,
						}}
					/>
				</PayPalScriptProvider>
			</div>
		</div>
	)
}
